// Simple translation utilities for React components
// Integrates with the existing Ghost translation system

let currentTranslations = {};
let currentLanguage = 'pt';

// Translation function for React components
function t(key, fallback = key) {
    return currentTranslations[key] || fallback;
}

// Load translations from the existing Ghost system
async function loadTranslations(lang) {
    try {
        // Use the global ghostTranslationUrls if available
        if (window.ghostTranslationUrls && window.ghostTranslationUrls[lang]) {
            const response = await fetch(window.ghostTranslationUrls[lang]);
            if (response.ok) {
                currentTranslations = await response.json();
                currentLanguage = lang;
                return currentTranslations;
            }
        }
    } catch (error) {
        console.warn(`Could not load translations for ${lang}:`, error);
    }
    return currentTranslations;
}

// React hook for translations
function useTranslation() {
    const [translations, setTranslations] = React.useState(currentTranslations);
    const [language, setLanguage] = React.useState(currentLanguage);

    React.useEffect(() => {
        // Get current language from localStorage
        const storedLang = localStorage.getItem('ghost-lang') || 'pt';
        if (storedLang !== language) {
            setLanguage(storedLang);
            loadTranslations(storedLang).then(setTranslations);
        }

        // Listen for language changes
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ghost-lang') || 'pt';
            if (newLang !== language) {
                setLanguage(newLang);
                loadTranslations(newLang).then(setTranslations);
            }
        };

        // Check for language changes periodically
        const interval = setInterval(handleLanguageChange, 1000);
        return () => clearInterval(interval);
    }, [language]);

    return { 
        t: (key, fallback = key) => translations[key] || fallback, 
        language 
    };
}

// Initialize with current language
if (typeof window !== 'undefined') {
    const initialLang = localStorage.getItem('ghost-lang') || 'pt';
    currentLanguage = initialLang;
    // Try to load translations if the global URLs are available
    if (window.ghostTranslationUrls) {
        loadTranslations(initialLang);
    }
}

// Get current language
function getCurrentLanguage() {
    return currentLanguage;
}

// Set language
function setLanguage(lang) {
    currentLanguage = lang;
    localStorage.setItem('ghost-lang', lang);
    loadTranslations(lang);
}

// Export for global access
window.translations = { t, useTranslation, loadTranslations, getCurrentLanguage, setLanguage };

export { t, useTranslation, loadTranslations, getCurrentLanguage, setLanguage };